import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, FileSignature, Eye } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import JeaCard from "@/components/jea/JeaCard";
import EmptyState from "@/components/jea/EmptyState";
import Skeleton from "@/components/jea/Skeleton";
import PillButton from "@/components/jea/PillButton";
import JeaInput from "@/components/jea/JeaInput";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { downloadAuthFile } from "@/lib/downloadFile";
import { centsToUSD, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const RANGES = [
  { key: "all", label: "All time" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

const HeaderCell = ({ children }) => (
  <th className="jea-caption text-text-secondary p-4 text-left">{children}</th>
);

const IconAction = ({ Icon, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 transition-colors inline-flex items-center justify-center"
    data-testid={testid}
  >
    <Icon size={14} />
  </button>
);

/**
 * Standalone /reports — cross-debtor list.
 * Columns: Report Date · Debtor · Brokers Run · Assets Found · Changes Detected · Cost · Actions
 */
const ReportsPage = () => {
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [search, setSearch] = useState("");

  const load = async (r = range) => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/reports?range=${r}`);
      setReports(data.reports || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const s = r.debtor_snapshot || {};
      return (s.full_name || "").toLowerCase().includes(q)
        || (s.case_number || "").toLowerCase().includes(q);
    });
  }, [reports, search]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="space-y-1">
          <div className="jea-caption">Enforcement</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Reports</h1>
          <p className="text-text-secondary text-[14px]">
            All broker-query reports across your debtors.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-[240px]">
            <JeaInput
              placeholder="Search by name or case number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="reports-search"
            />
          </div>
          <div className="flex gap-2" data-testid="reports-range-toggle">
            {RANGES.map((r) => (
              <PillButton
                key={r.key}
                active={range === r.key}
                onClick={() => setRange(r.key)}
                data-testid={`reports-range-${r.key}`}
              >
                {r.label}
              </PillButton>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <JeaCard className="p-0 overflow-hidden">
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        </JeaCard>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No reports yet"
          description="Run a query on any judgment to generate a report. Reports show consolidated assets, change logs, and enforcement recommendations."
          testid="reports-empty"
        />
      ) : (
        <JeaCard className="p-0 overflow-hidden" data-testid="reports-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default">
                <HeaderCell>Report Date</HeaderCell>
                <HeaderCell>Debtor</HeaderCell>
                <HeaderCell>Brokers Run</HeaderCell>
                <HeaderCell>Assets Found</HeaderCell>
                <HeaderCell>Changes Detected</HeaderCell>
                <HeaderCell>Cost</HeaderCell>
                <HeaderCell>Actions</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const snap = r.debtor_snapshot || {};
                const isHalt = !!r.hard_override;
                const stopClick = (e) => e.stopPropagation();
                return (
                  <motion.tr
                    key={r.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
                    className={cn(
                      "border-b border-border-default hover:bg-bg-tertiary transition-colors cursor-pointer",
                      isHalt && "bg-danger/5",
                    )}
                    onClick={() => nav(`/debtors/${r.debtor_id}/reports/${r.id}`)}
                    data-testid={`report-row-${r.id}`}
                  >
                    <td className="p-4 jea-date text-[13px]">{shortDate(r.generated_at)}</td>
                    <td className="p-4">
                      <div className="text-white font-semibold">{snap.full_name || "—"}</div>
                      <div className="jea-mono text-text-muted text-[11px]">{snap.case_number || ""}</div>
                    </td>
                    <td className="p-4 jea-mono text-white font-semibold" data-testid={`report-broker-count-${r.id}`}>
                      {r.broker_count ?? 0}
                    </td>
                    <td className="p-4 jea-mono text-white font-semibold" data-testid={`report-assets-count-${r.id}`}>
                      {r.assets_found_count ?? 0}
                    </td>
                    <td className="p-4" data-testid={`report-changes-count-${r.id}`}>
                      {r.changes_detected_count > 0 ? (
                        <span className="jea-mono font-bold text-warning">{r.changes_detected_count}</span>
                      ) : (
                        <span className="jea-mono text-text-muted">0</span>
                      )}
                    </td>
                    <td className="p-4 jea-mono text-accent-pink font-bold" data-testid={`report-cost-${r.id}`}>
                      {centsToUSD(r.total_cost_cents || 0)}
                    </td>
                    <td className="p-4" onClick={stopClick}>
                      <div className="flex items-center gap-2">
                        <IconAction Icon={Eye} label="View Report"
                          onClick={() => nav(`/debtors/${r.debtor_id}/reports/${r.id}`)}
                          testid={`report-action-view-${r.id}`} />
                        <IconAction Icon={Download} label="Download PDF"
                          onClick={async () => {
                            try { await downloadAuthFile(`/reports/${r.id}/pdf`); }
                            catch (e) { toast.error(formatApiError(e, "Couldn't download PDF.")); }
                          }}
                          testid={`report-action-pdf-${r.id}`} />
                        <IconAction Icon={FileSignature} label="Download Attorney Memo"
                          onClick={async () => {
                            try { await downloadAuthFile(`/reports/${r.id}/attorney-memo`); }
                            catch (e) { toast.error(formatApiError(e, "Couldn't download memo.")); }
                          }}
                          testid={`report-action-memo-${r.id}`} />
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </JeaCard>
      )}
    </div>
  );
};

export default ReportsPage;
